/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/accounts.ts`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useState } from 'react';
import { api } from './api';
import { useLiveTopic } from './live';
import type { Option } from '../components/Combobox';

export interface Account { account: string; name: string; type: string; grp: string; debit: number; credit: number; balance: number; hint?: string | null }

const cache = new Map<string, Promise<Account[]>>();
export const loadAccounts = (tid: string) => {
  if (!cache.has(tid)) cache.set(tid, api.get<Account[]>(`/api/t/${tid}/accounts`).catch((e) => { cache.delete(tid); throw e; }));
  return cache.get(tid)!;
};

/** After a Kontierungshinweis changes, the next picker loads the chart again. */
export const invalidateAccounts = (tid: string) => { cache.delete(tid); };

export function useAccountList(tid: string) {
  const [list, setList] = useState<Account[]>([]);
  const rev = useLiveTopic(tid, 'account', 'hint', 'setup');
  useEffect(() => {
    if (rev > 0) cache.delete(tid);
    loadAccounts(tid).then(setList).catch(() => {});
  }, [tid, rev]);
  return list;
}

export function useAccounts(tid: string): Record<string, string> {
  const list = useAccountList(tid);
  return Object.fromEntries(list.map((a) => [a.account, a.name]));
}

// The Kontierungshinweis is searchable in every account picker («Kaffeemaschine» finds 6100), not shown.
export const accountOptions = (list: Account[]): Option[] => list.map((a) => ({ value: a.account, label: `${a.account} ${a.name}`, keywords: a.hint ? `${a.grp} ${a.hint}` : a.grp }));

export const VAT_OPTIONS: Option[] = [
  { value: '', label: 'Ohne MWST' },
  { value: 'UN81', label: 'UN81', secondary: 'Umsatz 8.1 %' }, { value: 'UR26', label: 'UR26', secondary: 'Umsatz 2.6 %' }, { value: 'UB38', label: 'UB38', secondary: 'Beherbergung 3.8 %' },
  { value: 'UEX', label: 'UEX', secondary: 'Export befreit' }, { value: 'UAU', label: 'UAU', secondary: 'Leistung im Ausland' }, { value: 'UAN', label: 'UAN', secondary: 'ausgenommen' },
  { value: 'VM81', label: 'VM81', secondary: 'Vorsteuer Mat./DL 8.1 %' }, { value: 'VM26', label: 'VM26', secondary: 'Vorsteuer Mat./DL 2.6 %' },
  { value: 'VI81', label: 'VI81', secondary: 'Vorsteuer Inv./BA 8.1 %' }, { value: 'VI26', label: 'VI26', secondary: 'Vorsteuer Inv./BA 2.6 %' }, { value: 'VI38', label: 'VI38', secondary: 'Vorsteuer 3.8 %' },
  { value: 'BZ81', label: 'BZ81', secondary: 'Bezugsteuer 8.1 %' }, { value: 'BZ26', label: 'BZ26', secondary: 'Bezugsteuer 2.6 %' },
  { value: 'EUST', label: 'EUST', secondary: 'Einfuhrsteuer BAZG (auf 1170/1171)' }, { value: 'EE410', label: 'EE410', secondary: 'Einlageentsteuerung (auf 1170/1171)' },
  { value: 'VK415', label: 'VK415', secondary: 'Vorsteuerkorrektur (Haben 1170/1171)' }, { value: 'VK420', label: 'VK420', secondary: 'Vorsteuerkürzung (Haben 1170/1171)' },
  { value: 'NE900', label: 'NE900', secondary: 'Subventionen u. ä. (Ziffer 900)' }, { value: 'NE910', label: 'NE910', secondary: 'Spenden, Dividenden u. ä. (Ziffer 910)' },
  // Rates valid until 31.12.2023 (Art. 115 MWSTG: the rate follows the date of the supply).
  { value: 'UN77', label: 'UN77', secondary: 'Umsatz 7.7 % (bis 2023)' }, { value: 'UR25', label: 'UR25', secondary: 'Umsatz 2.5 % (bis 2023)' }, { value: 'UB37', label: 'UB37', secondary: 'Beherbergung 3.7 % (bis 2023)' },
  { value: 'VM77', label: 'VM77', secondary: 'Vorsteuer Mat./DL 7.7 % (bis 2023)' }, { value: 'VM25', label: 'VM25', secondary: 'Vorsteuer Mat./DL 2.5 % (bis 2023)' },
  { value: 'VI77', label: 'VI77', secondary: 'Vorsteuer Inv./BA 7.7 % (bis 2023)' }, { value: 'VI25', label: 'VI25', secondary: 'Vorsteuer Inv./BA 2.5 % (bis 2023)' }, { value: 'VI37', label: 'VI37', secondary: 'Vorsteuer 3.7 % (bis 2023)' },
  { value: 'BZ77', label: 'BZ77', secondary: 'Bezugsteuer 7.7 % (bis 2023)' }, { value: 'BZ25', label: 'BZ25', secondary: 'Bezugsteuer 2.5 % (bis 2023)' },
];

interface SaldoRate { code: string; label: string; rate: number }
interface TaxRow { validFrom: string | null; vatMethod: string; saldoRates: SaldoRate[] }

/** The MWST codes to offer on a date: the statutory ones and the Mandant's approved Saldosteuersätze then (VAT-04). */
export function vatOptions(rates: SaldoRate[]): Option[] {
  const at = VAT_OPTIONS.findIndex((o) => o.value === 'UN77');
  const s = rates.map((r) => ({ value: r.code, label: r.code, secondary: `Saldosteuersatz ${r.label} ${(r.rate / 100).toFixed(1)} %` }));
  return [...VAT_OPTIONS.slice(0, at), ...s, ...VAT_OPTIONS.slice(at)];
}

const settingsCache = new Map<string, Promise<{ base: TaxRow; changes: TaxRow[] }>>();
/** The approved Saldosteuersätze valid on a date (none under the effective method), from GET /vat/settings. */
export function useSaldoRatesAt(tid: string): (date: string) => SaldoRate[] {
  const [s, setS] = useState<{ base: TaxRow; changes: TaxRow[] } | null>(null);
  useEffect(() => {
    if (!settingsCache.has(tid)) settingsCache.set(tid, api.get<{ base: TaxRow; changes: TaxRow[] }>(`/api/t/${tid}/vat/settings`).catch((e) => { settingsCache.delete(tid); throw e; }));
    settingsCache.get(tid)!.then(setS).catch(() => {});
  }, [tid]);
  return (date: string) => {
    if (!s) return [];
    let cur = s.base;
    for (const c of [...s.changes].sort((a, b) => (a.validFrom ?? '').localeCompare(b.validFrom ?? ''))) if (c.validFrom && c.validFrom <= date) cur = c;
    return cur.vatMethod === 'saldo' ? cur.saldoRates : [];
  };
}
/** After the MWST settings change, the next picker loads them again. */
export const invalidateTaxSettings = (tid: string) => { settingsCache.delete(tid); };

const INPUT_TAX_ACCOUNTS = ['1170', '1171'];
const TAX_ONLY_DEBIT = ['EUST', 'EE410'];
const TAX_ONLY_CREDIT = ['VK415', 'VK420'];

/**
 * Which side of a two-line entry carries the MWST code. Input tax (V…), Bezugsteuer (BZ) and the debit tax-only codes
 * (EUST, EE410) sit on the debit line; the Vorsteuerkorrektur/-kürzung (VK415, VK420) on the credit line on 1170/1171;
 * turnover and Art. 18 Abs. 2 codes on the credit line.
 */
export function vatCodeSide(code: string, debit: string, credit: string): 'D' | 'C' | null {
  if (!code) return null;
  if (TAX_ONLY_DEBIT.includes(code)) return INPUT_TAX_ACCOUNTS.includes(debit) || !INPUT_TAX_ACCOUNTS.includes(credit) ? 'D' : 'C';
  if (TAX_ONLY_CREDIT.includes(code)) return INPUT_TAX_ACCOUNTS.includes(credit) || !INPUT_TAX_ACCOUNTS.includes(debit) ? 'C' : 'D';
  if (code.startsWith('V') || code.startsWith('BZ')) return 'D';
  return 'C';
}
